// Stable per-install device UUID, generated once and persisted in Capacitor
// Preferences (which iOS backs by NSUserDefaults; Android by SharedPreferences).
// On iOS, Preferences are auto-synced to iCloud Key-Value Store when the app
// uses CapacitorPreferences.set with the right group config — for now we keep
// it simple and re-mint on reinstall, which is acceptable for V1 (worst case
// the old device row goes stale and gets pruned).
//
// localStorage is used as a synchronous-read fallback so any code path that
// can't await the Preferences plugin (like the existing api.js token slot
// that we're removing) still has a value. In normal flow the Preferences
// value is the source of truth and localStorage is just a mirror.

import { Capacitor } from '@capacitor/core'
import { api } from './api.js'
import { setIsDevGlobal } from './game-store.js'

const STORAGE_KEY = 'mp-device-id'
const VERSION_KEY = 'mp-device-version'
const DEVICE_VERSION = 1

let cached = null
let registerPromise = null
let lastPingTs = 0

function generate() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback — vanishingly rare on modern WebViews. crypto.getRandomValues
  // is universally available, so this branch only triggers on very old
  // engines where randomUUID isn't yet present.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

async function readFromPreferences() {
  if (!Capacitor.isNativePlatform()) return null
  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value } = await Preferences.get({ key: STORAGE_KEY })
    return value || null
  } catch {
    return null
  }
}

async function writeToPreferences(id) {
  if (!Capacitor.isNativePlatform()) return
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.set({ key: STORAGE_KEY, value: id })
    await Preferences.set({ key: VERSION_KEY, value: String(DEVICE_VERSION) })
  } catch {}
}

// Resolve the device's stable UUID. First call on a fresh install mints a new
// one and persists it; subsequent calls hit the in-memory cache.
export async function getDeviceId() {
  if (cached) return cached

  // Try Preferences first (native) then localStorage mirror.
  const fromPrefs = await readFromPreferences()
  if (fromPrefs) {
    cached = fromPrefs
    try { localStorage.setItem(STORAGE_KEY, fromPrefs) } catch {}
    return cached
  }

  let fromLs = null
  try { fromLs = localStorage.getItem(STORAGE_KEY) } catch {}
  if (fromLs) {
    cached = fromLs
    // Promote to Preferences so the next launch reads from there.
    writeToPreferences(fromLs)
    return cached
  }

  // Fresh install — mint a new UUID.
  const fresh = generate()
  cached = fresh
  try { localStorage.setItem(STORAGE_KEY, fresh) } catch {}
  await writeToPreferences(fresh)
  return cached
}

// Synchronous read for code paths that need the ID immediately. Returns null
// on the very first cold start before getDeviceId() resolves; callers should
// still treat the async getter as the source of truth.
export function getDeviceIdSync() {
  if (cached) return cached
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}

const APP_VERSION = '1.0.0'

function platformName() {
  try { return Capacitor.getPlatform() } catch { return 'web' }
}

// Register-or-upsert the device row on the server. Idempotent on the
// server (INSERT … ON CONFLICT … DO UPDATE), so concurrent calls are
// safe. Pushes the returned is_dev flag into the shared store so any
// UI gated on it reacts without needing its own fetch.
//
// Concurrency rule: a tokenless call dedupes against an in-flight
// tokenless call — that's the StrictMode-double-mount case we want
// to absorb. A call WITH a pushToken always fires fresh, even if a
// tokenless register is in flight, otherwise the second call's
// token is silently dropped (the bug that left every device with
// no push_token in the database).
export async function registerDevice({ pushToken } = {}) {
  const hasToken = Boolean(pushToken)
  if (!hasToken && registerPromise) return registerPromise

  const p = (async () => {
    try {
      const id = await getDeviceId()
      const res = await api.registerDevice({
        device_id: id,
        push_token: pushToken || null,
        platform: platformName(),
        app_version: APP_VERSION,
      })
      if (res && typeof res.is_dev === 'boolean') {
        setIsDevGlobal(res.is_dev)
      }
      lastPingTs = Date.now()
      return res
    } catch (e) {
      console.warn('[device] register failed:', e?.message)
      return null
    }
  })()

  // Track only tokenless registrations against the dedupe slot —
  // a token-bearing call must never be dedupe-replaced by a future
  // tokenless one, and likewise a future tokenless call shouldn't
  // wait on a token-bearing one.
  if (!hasToken) {
    registerPromise = p
    p.finally(() => { if (registerPromise === p) registerPromise = null })
  }
  return p
}

// Lightweight heartbeat used on app foreground. Throttled — calling more
// often than once every 30 s is a no-op so we don't spam the worker when
// the user rapidly switches tabs.
export async function pingDevice() {
  if (Date.now() - lastPingTs < 30 * 1000) return null
  try {
    const id = await getDeviceId()
    const res = await api.pingDevice({ device_id: id })
    if (res && typeof res.is_dev === 'boolean') {
      setIsDevGlobal(res.is_dev)
    }
    lastPingTs = Date.now()
    return res
  } catch {
    return null
  }
}
